"use client";
import "@/firebase/config";

import HyperDX from "@hyperdx/browser";
import { onAuthStateChanged, signOut, User } from "firebase/auth";
import { fetchAndActivate, getRemoteConfig } from "firebase/remote-config";
import mixpanel from "mixpanel-browser";
import { ErrorBoundary } from "next/dist/client/components/error-boundary";
import { useCallback, useEffect, useMemo } from "react";
import { useState } from "react";
import { Provider } from "react-redux";
import { twMerge } from "tailwind-merge";

import { logout } from "@/app/api";
import { AlertCard } from "@/app/dashboard/alerts";
import Footer from "@/components/global/footer";
import Maintenance from "@/components/maintenance";
import { MobileSidebar } from "@/components/mobile-sidebar";
import { FeatureModal } from "@/components/release-modal";
import { NavMobile } from "@/components/salespage/mobile-nav";
import { NavBar } from "@/components/salespage/navbar";
import { SiteFooter } from "@/components/salespage/site-footer";
import Sidebar from "@/components/sidebar";
import { LottieWrapper } from "@/components/ui/lottie-wrapper";
import { Toaster } from "@/components/ui/sonner";
import { WelcomeModal } from "@/components/welcome-modal";
import firebaseApp, { auth } from "@/firebase/config";
import { getRemoteConfigValue } from "@/lib/firebaseRemoteConfig";
import { usePathname, useRouter } from "@/lib/i18n";
import { mixpanelSignout } from "@/lib/tracker";
import { cn } from "@/lib/utils";
import { PUBLIC_PATHS } from "@/middlewares/auth";
import * as m from "@/paraglide/messages";
import { store } from "@/services/redux/store";

import "moment/locale/fr";

import * as animationData from "../../../public/animations/loadingAnimation.json";
import { fullMenu } from "../constants/sidebar";
import {
  getAvailableProviders,
  getConnectedProviders,
} from "../controllers/exchanges";
import { syncPortfolios } from "../controllers/orders";
import { getUserData, logoutBE } from "../controllers/user";
import { ExchangeKey, db } from "../db";
import { usePWA } from "../hooks/PWA.hooks";
import {
  setProviders,
  toggleFetched,
  toggleLoadingKeys,
} from "../redux/exchange-switcher.slice";
import { setSidebarOptions } from "../redux/sidebar.slice";
import { toggleSyncLoading } from "../redux/sync.slice";

const defaultOptions = {
  loop: true,
  autoplay: true,
  animationData: animationData,
  rendererSettings: {
    preserveAspectRatio: "xMidYMid slice",
  },
};

export const fetchConnectedKeys = (set?: React.Dispatch<ExchangeKey[]>) => {
  getConnectedProviders({}).then((response) => {
    if (set) set(response.data);
  });
};

export const getProviders = (controller?: AbortController) => {
  getAvailableProviders({
    signal: controller ? controller.signal : undefined,
  }).then((response) => {
    store.dispatch(setProviders(response.data.data));
  });
};

export default function ClientWrapper({
  children,
}: Readonly<{
  children: React.ReactNode;
}>) {
  usePWA();
  const { replace, push } = useRouter();
  const pathname = usePathname();

  const [user, setUser] = useState<User>();
  const [isCollapsed, setIsCollapsed] = useState<boolean>(false);
  const [isLoadingKeys, setIsLoadingKeys] = useState<boolean>(false);
  const [isConfigLoaded, setIsConfigLoaded] = useState<boolean>(false);
  const [underMaintenance, setUnderMaintenance] = useState<boolean>(false);
  const [isAdmin, setIsAdmin] = useState<boolean>();
  const [syncLoading, setSyncLoading] = useState<boolean>();
  const [connectedKeys, setConnectedKeys] = useState<ExchangeKey[]>();
  const [modalConfig, setModalConfig] = useState({});

  const logoutAction = useCallback(
    async (event?: any) => {
      if (event?.detail?.withBELogout !== false) {
        await logoutBE();
      }
      signOut(auth).then(async () => {
        await logout();
        // Fully reset the local Dexie DB on logout so no user's holdings /
        // analytics / snapshots / keys leak into the next session. Previously
        // only `selectedExchange` was cleared, which left stale portfolio rows
        // behind and forced a manual /debug/clear to recover.
        await db.resetDatabase();
        HyperDX.setGlobalAttributes({
          userId: "",
          userEmail: "",
          userName: "",
        });
        mixpanelSignout();
        setIsAdmin(undefined);
        push("/signin");
        localStorage.clear();
        setTimeout(() => {
          store.dispatch({ type: "clear" });
        }, 500);
      });
    },
    [push],
  );

  useEffect(() => {
    const controller = new AbortController();
    const subscription = onAuthStateChanged(auth, (authedUser) => {
      if (authedUser?.uid != null) {
        setUser(authedUser);
        HyperDX.setGlobalAttributes({
          userId: authedUser.uid,
          userEmail: authedUser.email ?? "",
          userName: authedUser.displayName ?? "",
        });
        if (isAdmin == null) {
          store.dispatch(setSidebarOptions(fullMenu(m)));
          getUserData({
            signal: controller.signal,
          }).then((response) => {
            setIsAdmin(response?.role?.toLowerCase() === "admin");
            if (!["/inneos", "/kevin"].includes(pathname)) {
              if (
                response.language === "fr" &&
                !window.location.pathname.includes("/fr")
              ) {
                replace(pathname, {
                  locale: "fr",
                });
              }
              if (
                response.language === "en" &&
                window.location.pathname.includes("/fr")
              ) {
                replace(pathname, {
                  locale: "en",
                });
              }
            }
          });
        }
      } else {
        setUser(undefined);
        if (!PUBLIC_PATHS.includes(pathname)) logoutAction();
      }
    });
    return () => {
      controller.abort();
      if (subscription) subscription();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, user, isAdmin]);

  useEffect(() => {
    window.addEventListener("logoutNow", logoutAction);

    return () => {
      window.removeEventListener("logoutNow", logoutAction);
    };
  }, [logoutAction]);

  useEffect(() => {
    if (!isLoadingKeys && user != null) {
      fetchConnectedKeys(setConnectedKeys);
    }
  }, [isLoadingKeys, user]);

  useEffect(() => {
    const controller = new AbortController();
    getProviders();
    HyperDX.init({
      apiKey: "5a2da645-2240-4c07-9dff-6ec67631ce76",
      service: "Arya-crypto",
      tracePropagationTargets: [process.env.NEXT_PUBLIC_BASE_URL!], // Set to link traces from frontend to backend requests
      consoleCapture: true, // Capture console logs (default false)
      advancedNetworkCapture: true, // Capture full HTTP request/response headers and bodies (default false)
    });

    HyperDX.attachToReactErrorBoundary(ErrorBoundary);

    mixpanel.init(process.env.NEXT_PUBLIC_MIXPANEL_KEY!, {
      api_host: process.env.NEXT_PUBLIC_MIXPANEL_PROXY_DOMAIN!,
      debug: false,
      track_pageview: true,
      disable_persistence: true,
      ignore_dnt: true,
    });

    const remoteConfig = getRemoteConfig(firebaseApp);
    remoteConfig.settings.minimumFetchIntervalMillis = 3600;
    fetchAndActivate(remoteConfig).then(() => {
      setUnderMaintenance(getRemoteConfigValue("under_maintenenace"));
      setModalConfig(getRemoteConfigValue("modal"));
      setIsConfigLoaded(true);
    });

    return () => {
      controller.abort();
    };
  }, []);

  const syncStatusApi = useCallback(async () => {
    if (user?.uid) {
      const {
        data: { data },
      }: any = await syncPortfolios();
      if (data !== "IN_PROGRESS") {
        if (isLoadingKeys && syncLoading) {
          store.dispatch(toggleFetched(false));
          store.dispatch(toggleLoadingKeys(true));
          store.dispatch(toggleSyncLoading(false));
        }
        return;
      } else {
        if (!syncLoading) store.dispatch(toggleSyncLoading(true));
        setTimeout(() => {
          syncStatusApi();
        }, 2000);
      }
    }
  }, [isLoadingKeys, user?.uid, syncLoading]);

  useEffect(() => {
    window.addEventListener("sync-portfolios", syncStatusApi);

    return () => {
      window.removeEventListener("sync-portfolios", syncStatusApi);
    };
  }, [syncStatusApi]);

  useEffect(() => {
    store.subscribe(() => {
      setIsCollapsed(store.getState().exchangeSwitcher.isSideBarCollapsed);
    });
    store.subscribe(() => {
      setIsLoadingKeys(store.getState().exchangeSwitcher.fetched);
    });
    store.subscribe(() => {
      setSyncLoading(store.getState().sync.loading);
    });
  }, []);

  // Check if the path is a smart allocation template path with no user
  const isSmartAllocationTemplateWithNoUser = useCallback(() => {
    return pathname?.startsWith("/smart-allocation/templates/") && !user?.uid;
  }, [pathname, user?.uid]);

  const shouldShowGlobalComponents = useCallback(
    (extra: string[]) => {
      // Hide global components for smart allocation template without user
      if (isSmartAllocationTemplateWithNoUser()) {
        return false;
      }

      return ![
        "/lifetime-offer",
        "/terms",
        "/privacy",
        "/inneos",
        "/kevin",
        "/pricing",
        "/login",
        "/signup",
        "/crypto-profit-calculator",
        "/coin-converter",
        "/signin",
        "/register",
        "/payment",
        "/",
        "/reset-password",
        "/forgot-password",
        "/checkout",
        "/debug",
        "/debug/clear",
        "/trump",
        "/fr/trump",
        "/kevin",
        "/fr/kevin",
        "/trial",
        "/fr/trial",
        "/upgrade",
        "/fr/upgrade",
        "/blackfriday",
        "/fr/blackfriday",
        "/offline",
        "/fr/offline",
        "/signin/2fa-confirmation",
        "/fr/signin/2fa-confirmation",
        ...extra,
      ].includes(pathname ?? "");
    },
    [pathname, isSmartAllocationTemplateWithNoUser],
  );

  const shouldShowSalesComponents = useCallback(
    (extra: string[]) => {
      // Hide sales components for smart allocation template without user
      if (isSmartAllocationTemplateWithNoUser()) {
        return false;
      }

      return [
        "/terms",
        "/privacy",
        "/crypto-profit-calculator",
        "/coin-converter",
        "/pricing",
        "/payment",
        "/",
        "/upgrade",
        "/fr/upgrade",
        ...extra,
      ].includes(pathname ?? "");
    },
    [pathname, isSmartAllocationTemplateWithNoUser],
  );

  const footer = useMemo(() => {
    return shouldShowGlobalComponents([!user?.uid ? "/market" : ""]) ? (
      <Footer />
    ) : shouldShowSalesComponents([!user?.uid ? "/market" : ""]) ? (
      <>
        <SiteFooter />
      </>
    ) : (
      <></>
    );
  }, [shouldShowGlobalComponents, shouldShowSalesComponents, user?.uid]);

  const header = useMemo(() => {
    return shouldShowGlobalComponents([!user?.uid ? "/market" : ""]) ? (
      <></>
    ) : shouldShowSalesComponents([!user?.uid ? "/market" : ""]) ? (
      <div className="relative">
        <NavMobile />
        <NavBar scroll={true} />
      </div>
    ) : (
      <></>
    );
  }, [shouldShowGlobalComponents, shouldShowSalesComponents, user?.uid]);

  const sidebar = useMemo(() => {
    return (
      <>
        {shouldShowGlobalComponents([user?.uid ? "" : "/market"]) ? (
          <>
            <Sidebar />

            <div className="md:mt-8 mt-4 ms-4 lg:hidden absolute">
              <MobileSidebar />
            </div>
          </>
        ) : (
          <></>
        )}
      </>
    );
  }, [shouldShowGlobalComponents, user?.uid]);

  if (!isConfigLoaded) {
    return (
      <div className="w-full h-screen flex items-center justify-center">
        <LottieWrapper options={defaultOptions} width={100} height={100} />
      </div>
    );
  }

  if (underMaintenance == true && !isAdmin) {
    return <Maintenance />;
  }

  return (
    <Provider store={store}>
      {header}
      {sidebar}
      <main
        className={cn(
          "flex flex-1 flex-col gap-4",
          ![
            "/",
            "/inneos",
            "/kevin",
            "/pricing",
            !user?.uid ? "/market" : "",
            "/signin",
            "/signup",
            "/reset-password",
            "/coin-converter",
            "/crypto-profit-calculator",
            "/upgrade",
            "/trial",
            "/fr/trial",
            "/blackfriday",
            "/fr/blackfriday",
            "/offline",
            "/fr/offline",
          ].includes(pathname)
            ? "p-2 md:px-6 md:py-4"
            : "",
        )}
      >
        <div
          className={twMerge(
            shouldShowGlobalComponents([user?.uid ? "" : "/market"]) &&
              (isCollapsed ? "lg:ps-[70px]" : "lg:ps-[250px]"),
            "w-full",
          )}
          suppressHydrationWarning={true}
        >
          {children}
          {footer}
        </div>
        <Toaster visibleToasts={4} duration={3000} />
        {connectedKeys?.filter(
          (e) => e.key_status_name !== "VALID" && e.provider_id != null,
        ).length ? (
          <AlertCard />
        ) : (
          <></>
        )}

        {pathname === "/dashboard" ? (
          <>
            <FeatureModal modalConfig={modalConfig} />
            <WelcomeModal />
          </>
        ) : (
          <> </>
        )}
      </main>
    </Provider>
  );
}
